// ONLY `complete` IS DONE.
import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../api";
import { qk } from "./queries";
import { useUiStore } from "../stores/uiStore";
import { useMergeWatch, isWatching } from "../stores/mergeWatchStore";
import { purgeFieldLocalData } from "../lib/fieldLocalData";

const POLL_MS = 5000;

export function useMergeWatcher(enabled = true) {
  const qc = useQueryClient();
  const byId = useMergeWatch((s) => s.byId);
  // Poll on the SET of watched ids rather than on `byId` itself: the object identity changes on every
  // status write, which would tear down and restart the interval on each tick.
  const watching = Object.values(byId)
    .filter(isWatching)
    .map((w) => w.merge_id)
    .sort()
    .join(",");

  useEffect(() => {
    if (!enabled || !watching) return;
    let alive = true;

    const tick = async () => {
      for (const id of watching.split(",")) {
        try {
          const rec = await api.mergeStatus(id);
          if (!alive) return;
          const store = useMergeWatch.getState();
          if (rec.status === "complete") {
            // This happens HERE and not at authorise: until compute commits the destination the
            // sources are untouched and fully usable, so purging early would have destroyed real
            // work on a merge that then failed.
            const w = store.byId[id];
            for (const fid of w?.source_field_ids ?? rec.source_field_ids ?? [])
              purgeFieldLocalData(fid);
            const ui = useUiStore.getState();
            if (
              ui.mapOpen &&
              (w?.source_field_ids ?? []).some((f) =>
                ui.mapFieldIds.includes(f),
              )
            )
              ui.closeMap();
            store.dismiss(id);
            void qc.invalidateQueries({ queryKey: qk.fields });
            void qc.invalidateQueries({ queryKey: ["layers"] });
            void qc.invalidateQueries({ queryKey: ["field-jobs"] });
          } else if (rec.status === "failed") {
            // Keep it on screen with the reason. Nothing was combined and both sources are intact
            // which is worth saying explicitly — the user authorised something irreversible.
            store.update(id, {
              status: "failed",
              failure_reason: rec.failure_reason ?? null,
            });
            void qc.invalidateQueries({ queryKey: qk.fields });
          } else {
            store.update(id, { status: rec.status });
          }
        } catch {
          // A failed POLL is not a failed merge — the work is server-side and carries on. Reporting a
          // failure here would be a claim we cannot support, so keep watching.
        }
      }
    };

    void tick();
    const t = setInterval(() => void tick(), POLL_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, [watching, qc, enabled]);
}
